/**
 * BaseSidebar - Sidebar unificado com componentes compartilhados
 *
 * Substitui ~1,000 linhas de código duplicado em:
 * - AdminSidebar
 * - PartnerSidebar
 * - AssessorSidebar
 * - InvestorSidebar
 *
 * @example
 * <BaseSidebar
 *   navigationItems={[{ label: 'Dashboard', href: '/admin/dashboard', icon: <LayoutDashboard /> }]}
 *   profilePath="/admin/profile"
 *   onSidebarToggle={handleToggle}
 * />
 */

import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import {
  AnimatedSidebar,
  SidebarBody,
  SidebarLink,
  useSidebar,
} from '@/components/ui/animated-sidebar';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext.hook';
import { useScreenSize } from '@/hooks/use-mobile';
import { UserAvatar } from '@/components/ui/UserAvatar';
import logoFull from '@/assets/logo-branca-sem-descricao.webp';
import logoIcon from '@/assets/brava-icon.png';

// =============================================================================
// TYPES
// =============================================================================

export interface NavigationItem {
  label: string;
  href: string;
  icon: React.ReactNode;
}

export interface BaseSidebarProps {
  /** Items de navegação */
  navigationItems: NavigationItem[];
  /** Caminho para a página de perfil */
  profilePath: string;
  /** Callback quando sidebar abre/fecha */
  onSidebarToggle?: (open: boolean) => void;
  /** Gap entre seções (default: 20) */
  bodyGap?: number;
}

// =============================================================================
// SHARED COMPONENTS
// =============================================================================

/**
 * Logo animado que colapsa junto com o sidebar
 */
export const SidebarLogo: React.FC = () => {
  const { open, animate } = useSidebar();

  return (
    <div className="font-normal flex items-center justify-start text-sm py-1 relative z-20">
      <motion.div
        animate={{
          width: animate ? (open ? 'auto' : 40) : 'auto',
        }}
        transition={{
          duration: 0.3,
          ease: 'easeInOut',
        }}
        className="overflow-hidden flex items-center"
      >
        <img
          src={animate ? (open ? logoFull : logoIcon) : logoFull}
          alt="BRAVAFORTE"
          loading="lazy"
          decoding="async"
          className="h-10 w-auto object-contain"
          style={{ minWidth: 10 }}
        />
      </motion.div>
    </div>
  );
};

/**
 * Link de navegação animado
 */
export const SidebarNavLink: React.FC<{
  link: NavigationItem;
  isActive?: boolean;
}> = ({ link, isActive: externalIsActive }) => {
  const { open, animate, setOpen } = useSidebar();
  const { isMobile } = useScreenSize();

  const handleClick = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  return (
    <NavLink
      to={link.href}
      onClick={handleClick}
      className={({ isActive }) =>
        cn(
          'flex items-center justify-start gap-2 group/sidebar py-1.5 px-1.5 text-sidebar-foreground rounded-md transition-colors',
          isActive || externalIsActive
            ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
            : 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
        )
      }
    >
      <div className="shrink-0">{link.icon}</div>
      <motion.span
        animate={{
          opacity: animate ? (open ? 1 : 0) : 1,
          width: animate ? (open ? 'auto' : 0) : 'auto',
        }}
        transition={{
          duration: 0.3,
          ease: 'easeInOut',
        }}
        className="text-sm group-hover/sidebar:translate-x-1 transition duration-150 whitespace-nowrap overflow-hidden p-0! m-0!"
      >
        {link.label}
      </motion.span>
    </NavLink>
  );
};

/**
 * Perfil do usuário no sidebar
 */
export const SidebarUserProfile: React.FC<{
  profile: {
    first_name: string;
    last_name: string;
    email: string;
    avatar_url?: string | null;
  } | null;
  profilePath: string;
}> = ({ profile, profilePath }) => {
  const { open, animate, setOpen } = useSidebar();
  const { isMobile } = useScreenSize();

  if (!profile) return null;

  const handleClick = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  return (
    <NavLink
      to={profilePath}
      onClick={handleClick}
      className="py-2 -ml-1 rounded-md flex items-center gap-3 hover:bg-sidebar-accent transition-colors cursor-pointer"
    >
      <div className="shrink-0">
        <UserAvatar
          avatarUrl={profile.avatar_url}
          firstName={profile.first_name}
          lastName={profile.last_name}
          email={profile.email}
          size="md"
        />
      </div>
      <motion.div
        animate={{
          opacity: animate ? (open ? 1 : 0) : 1,
          width: animate ? (open ? 'auto' : 0) : 'auto',
        }}
        transition={{
          duration: 0.3,
          ease: 'easeInOut',
        }}
        className="flex-1 min-w-0 overflow-hidden"
      >
        <div className="text-sm text-sidebar-foreground font-medium truncate">
          {profile.first_name} {profile.last_name}
        </div>
        <div className="text-xs text-sidebar-foreground/70 truncate">
          {profile.email}
        </div>
      </motion.div>
    </NavLink>
  );
};

/**
 * Botão de logout
 */
export const SidebarLogout: React.FC<{ onSignOut: () => void }> = ({
  onSignOut,
}) => {
  const { setOpen } = useSidebar();
  const { isMobile } = useScreenSize();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isMobile) {
      setOpen(false);
    }
    onSignOut();
  };

  return (
    <SidebarLink
      link={{
        label: 'Sair',
        href: '#',
        icon: <LogOut className="text-sidebar-foreground h-5 w-5 shrink-0" />,
      }}
      onClick={handleClick}
    />
  );
};

// =============================================================================
// WRAPPER COMPONENT
// =============================================================================

const SidebarWrapper: React.FC<{
  children: React.ReactNode;
  onSidebarToggle?: (open: boolean) => void;
}> = ({ children, onSidebarToggle }) => {
  const [open, setOpen] = React.useState(false);

  const handleSetOpen = React.useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      onSidebarToggle?.(newOpen);
    },
    [onSidebarToggle]
  );

  return (
    <AnimatedSidebar open={open} setOpen={handleSetOpen} animate>
      {children}
    </AnimatedSidebar>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const BaseSidebar: React.FC<BaseSidebarProps> = ({
  navigationItems,
  profilePath,
  onSidebarToggle,
  bodyGap = 20,
}) => {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/');
    }
  };

  return (
    <SidebarWrapper onSidebarToggle={onSidebarToggle}>
      <SidebarBody className={`justify-between gap-${bodyGap}`}>
        <div className="flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
          <SidebarLogo />

          <div className="mt-8 flex flex-col gap-2">
            {navigationItems.map((item, idx) => (
              <SidebarNavLink key={idx} link={item} />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <SidebarUserProfile profile={profile} profilePath={profilePath} />
          <SidebarLogout onSignOut={handleSignOut} />
        </div>
      </SidebarBody>
    </SidebarWrapper>
  );
};

export default BaseSidebar;
